import { useAuth } from '@/app/_auth/auth-hooks'
import { RateLimit } from '@/app/_common/rate-limit'
import { RateLimitIncreaseDialog } from '@/app/_common/rate-limit-increase-dialog'
import { Button } from '@/components/ui/button'
import { cn, isVPCMode } from '@/lib/utils'
import { T, useTranslate, type PropsWithKeyName, type TranslationKey } from '@tolgee/react'
import React from 'react'
import { useLocalStorage } from 'react-use'
import { DomainSelect } from '../_common/domain/domain-select'
import { APIKeySection } from './_components/api-key-section'
import { ConsentDialog } from './_components/consent-dialog'
import { ContactDialog } from './_components/contact-dialog'
import { DeactivateDialog } from './_components/deactivate-dialog'
import { TermlyScriptLoader } from './_components/termly-script-loader'
import { PageTemplate } from '../_common/page-template'

function Settings() {
  const { t } = useTranslate()
  const { user } = useAuth()
  const [isDpaAccepted, setIsDpaAccepted] = useLocalStorage(
    `emergence-dpa-accepted${user?.uid ? `-${user.uid}` : ''}`,
    false,
  )

  return (
    <>
      <PageTemplate.Header>
        <PageTemplate.H2>
          <T keyName='navigation.settings.title' />
        </PageTemplate.H2>
      </PageTemplate.Header>

      <div className='relative mx-auto flex w-full max-w-[1140px] grow flex-col justify-between gap-6'>
        <div className='flex flex-col gap-6'>
          <Grid>
            {!isVPCMode && (
              <Card row>
                <CardHeading keyName='account.apiKey.title' />
                <APIKeySection />
              </Card>
            )}
            {!isVPCMode && (
              <Card row>
                <CardHeading keyName='account.rateLimit.title' />
                <div className='flex flex-wrap items-center gap-2'>
                  <RateLimit className='font-circular-xx' />
                  <RateLimitIncreaseDialog>
                    <Button
                      size='sm'
                      variant='mango-outline'
                      title={t('account.rateLimit.button.a11y')}
                      className='min-w-32'
                    >
                      <T keyName='account.rateLimit.button.title' />
                    </Button>
                  </RateLimitIncreaseDialog>
                </div>
              </Card>
            )}
            <Card row>
              <CardHeading keyName='account.consent.title' />
              <ConsentDialog>
                <Button
                  size='sm'
                  variant='mango-outline'
                  title={t('account.consent.button.a11y')}
                  className='min-w-32'
                >
                  <T keyName='account.consent.button.title' />
                </Button>
              </ConsentDialog>
            </Card>
            <Card row>
              <CardHeading keyName='account.domain.title' />
              <DomainSelect />
            </Card>
            <Card row>
              <CardHeading keyName='account.contact.title' />
              <div className='flex flex-wrap items-center gap-2'>
                <span className='text-sm font-light'>
                  <T keyName='account.contact.body' />
                </span>
                <ContactDialog>
                  <Button
                    size='sm'
                    variant='mango-outline'
                    title={t('account.contact.button.a11y')}
                    className='min-w-32'
                  >
                    <T keyName='account.contact.button.title' />
                  </Button>
                </ContactDialog>
              </div>
            </Card>
            <Card row>
              <CardHeading keyName='account.deactivate.title' />
              <DeactivateDialog>
                <Button
                  variant='red-outline'
                  size='sm'
                  title={t('account.deactivate.button.a11y')}
                  className='min-w-32'
                >
                  <T keyName='account.deactivate.button.title' />
                </Button>
              </DeactivateDialog>
            </Card>
          </Grid>
          <Card>
            <div className='flex flex-wrap'>
              <UserAttribute labelKey='account.userProfile.sections.name' value={user?.fullName} />
              <UserAttribute labelKey='account.userProfile.sections.email' value={user?.email} />
              <UserAttribute labelKey='account.userProfile.sections.title' value={user?.role} />
              <UserAttribute
                labelKey='account.userProfile.sections.organization'
                value={user?.organization}
              />
            </div>
          </Card>
        </div>
        <div className='flex flex-col gap-6'>
          {!isDpaAccepted && (
            <div className='flex items-center gap-3 rounded-2xl border border-neutral-50 bg-neutral-25 py-4 pl-6 pr-3 shadow-sidebar'>
              <div className='text-base font-light'>
                <span className='font-bold'>Legal Notice:</span> Our Data Processing Agreement (DPA)
                is being updated effective September 15, 2025. View the current{' '}
                <FooterAnchor
                  inline
                  href={import.meta.env.VITE_EMERGENCE_DPA_URL}
                  target='_blank'
                  rel='noreferrer'
                  title='Emergence Data Processing Addendum'
                >
                  DPA
                </FooterAnchor>
                {' and '}
                <FooterAnchor
                  inline
                  href={import.meta.env.VITE_EMERGENCE_DPA_UPDATE_URL}
                  target='_blank'
                  rel='noreferrer'
                  title='Upcoming Emergence Data Processing Addendum'
                >
                  Upcoming Changes
                </FooterAnchor>
                {'.'}
              </div>
              <Button size='sm' onClick={() => setIsDpaAccepted(true)}>
                Ok
              </Button>
            </div>
          )}
          <div className='flex flex-wrap items-end justify-end gap-4 text-sm'>
            <FooterAnchor
              href={import.meta.env.VITE_TERMS_OF_SERVICE_URL}
              target='_blank'
              rel='noreferrer'
              title={t('navigation.terms.a11y')}
            >
              <T keyName='navigation.terms.title' />
            </FooterAnchor>
            <FooterAnchor
              href={import.meta.env.VITE_PRIVACY_POLICY_URL}
              target='_blank'
              rel='noreferrer'
              title={t('navigation.privacyPolicy.a11y')}
            >
              <T keyName='navigation.privacyPolicy.title' />
            </FooterAnchor>
            <TermlyScriptLoader
              renderLink={(props) => (
                <FooterAnchor title={t('navigation.cookie.a11y')} {...props}>
                  <T keyName='navigation.cookie.title' />
                </FooterAnchor>
              )}
            />
            {isDpaAccepted && (
              <>
                <FooterAnchor
                  href={import.meta.env.VITE_EMERGENCE_DPA_URL}
                  target='_blank'
                  rel='noreferrer'
                  title='Emergence Data Processing Addendum'
                >
                  DPA
                </FooterAnchor>
                <FooterAnchor
                  href={import.meta.env.VITE_EMERGENCE_DPA_UPDATE_URL}
                  target='_blank'
                  rel='noreferrer'
                  title='Upcoming Emergence Data Processing Addendum'
                >
                  DPA Upcoming Changes
                </FooterAnchor>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// 👇 layout components

const UserAttribute = ({
  labelKey,
  value,
}: {
  labelKey: TranslationKey
  value?: string | null
}) => {
  if (!value) return

  return (
    <div className='flex min-h-20 min-w-[180px] flex-1 shrink-0 flex-col justify-center gap-1 overflow-hidden px-6 py-4 text-neutral-500'>
      <div className='font-neue-machina text-sm font-normal'>
        <T keyName={labelKey} />
      </div>
      <span className='overflow-clip text-ellipsis text-sm font-bold'>{value}</span>
    </div>
  )
}

const Card = ({ children, className, row }: ComponentProps<{ row?: boolean }>) => (
  <div
    className={cn(
      'w-full rounded-2xl border border-neutral-75 bg-neutral-white',
      row &&
        'flex min-h-20 flex-col flex-wrap items-start gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between lg:flex-col lg:items-start 3xl:flex-row 3xl:items-center',

      className,
    )}
  >
    {children}
  </div>
)

const CardHeading = ({
  keyName,
  params,
  className,
}: Readonly<{ className?: string } & PropsWithKeyName>) => (
  <h3 className={cn('font-neue-machina text-sm font-normal', className)}>
    <T keyName={keyName} params={params} />
  </h3>
)

const Grid = ({ children, className }: ComponentProps) => (
  <div className={cn('grid w-full gap-6 lg:grid-cols-2', className)}>{children}</div>
)

const FooterAnchor = ({
  children,
  className,
  inline,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & Readonly<{ inline?: boolean }>) => (
  <a
    className={cn(
      'inline-flex w-fit cursor-pointer items-start whitespace-nowrap rounded-sm font-light text-mango-300 underline outline-none ring-fig-300 ring-offset-1 focus:ring',
      className,
    )}
    {...props}
  >
    {children}
  </a>
)

export { Settings }
